/* Talks to the API. Every call goes through /api, which the dev server proxies to the
   Fastify process. Errors arrive as { error: "message" } and are re-thrown with that
   message so screens can show it directly. */

/* In development the Vite dev server proxies /api to the API process. In production one
   container serves both, so calls go to the same origin with no prefix. */
const BASE = import.meta.env.DEV ? "/api" : "";

/* For links the browser navigates to directly, such as CSV downloads. */
export const apiUrl = path => BASE + path;

async function request(path, { method = "GET", body, form } = {}) {
  const opts = { method, credentials: "same-origin", headers: {} };
  if (form) opts.body = form;
  else if (body !== undefined) {
    opts.headers["content-type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(BASE + path, opts);
  const isJson = (res.headers.get("content-type") || "").includes("application/json");
  const payload = isJson ? await res.json().catch(() => ({})) : await res.text();
  if (!res.ok) {
    const err = new Error(isJson ? payload.error || "Something went wrong" : "Something went wrong");
    err.status = res.status;
    throw err;
  }
  return payload;
}

export const api = {
  me: () => request("/me"),
  requestCode: email => request("/auth/email", { method: "POST", body: { email } }),
  verifyCode: (email, code) => request("/auth/verify", { method: "POST", body: { email, code } }),
  google: credential => request("/auth/google", { method: "POST", body: { credential } }),
  signOut: () => request("/auth/signout", { method: "POST" }),
  startGuest: () => request("/auth/guest", { method: "POST" }),

  projects: () => request("/projects"),
  createProject: name => request("/projects", { method: "POST", body: { name } }),
  deleteProject: id => request(`/projects/${id}`, { method: "DELETE" }),
  latestRun: id => request(`/projects/${id}/latest-run`),

  lead: payload => request("/leads", { method: "POST", body: payload }),

  upload: (projectId, kind, file) => {
    const form = new FormData();
    form.append("kind", kind);
    form.append("file", file, file.name);
    return request(`/projects/${projectId}/uploads`, { method: "POST", form });
  },
  startRun: (projectId, queriesUploadId, pagesUploadId) =>
    request(`/projects/${projectId}/runs`, { method: "POST", body: { queriesUploadId, pagesUploadId } }),
  run: id => request(`/runs/${id}`),
  summary: id => request(`/runs/${id}/summary`),

  queries: (runId, params) => request(`/runs/${runId}/queries?${new URLSearchParams(params)}`),
  queryDetail: (runId, idx) => request(`/runs/${runId}/queries/${idx}`),
  facets: runId => request(`/runs/${runId}/facets`),
  aggregates: (runId, by) => request(`/runs/${runId}/aggregates?by=${by}`),
  insights: runId => request(`/runs/${runId}/insights?scope=journey`),
  actions: runId => request(`/runs/${runId}/actions`),
};
